/**
 * PlanPreviewDialog - Weekly calendar preview of AI-generated training plan
 *
 * Displays the generated plan in a weekly calendar view before confirmation.
 */

import {
  DirectionsBike as BikeIcon,
  Close as CancelIcon,
  Check as ConfirmIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  Weekend as RestIcon,
  DirectionsRun as RunIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import NoteInfoModal from '../common/NoteInfoModal';

interface AIGeneratedSession {
  date: string;
  dayOfWeek: string;
  sessionType: string;
  sport: 'bike' | 'run' | 'rest';
  durationMin: number;
  predictedTSS: number;
  intensity: 'recovery' | 'endurance' | 'tempo' | 'threshold' | 'vo2max';
  indoor: boolean;
  intensityNotes: string;
  note?: string; // Session-level explanation
}

interface AIGeneratedWeek {
  weekNumber: number;
  weekStartDate: string;
  weekEndDate: string;
  targetTSS: number;
  actualTSS: number;
  sessions: AIGeneratedSession[];
  note?: string; // Week-level explanation
}

interface ScheduleAgentResponse {
  planStartDate: string;
  planEndDate: string;
  totalWeeks: number;
  note?: string; // Plan-level explanation (overall training philosophy)
  weeks: AIGeneratedWeek[];
  summary: {
    totalTSS: number;
    avgWeeklyTSS: number;
    totalSessions: number;
    restDays: number;
  };
}

interface PlanPreviewDialogProps {
  open: boolean;
  plan: ScheduleAgentResponse;
  isMock?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const INTENSITY_COLORS: Record<string, string> = {
  recovery: '#4caf50',
  endurance: '#2196f3',
  tempo: '#ff9800',
  threshold: '#f44336',
  vo2max: '#9c27b0',
};

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const PlanPreviewDialog: React.FC<PlanPreviewDialogProps> = ({
  open,
  plan,
  isMock = false,
  onConfirm,
  onCancel,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentWeekIndex, setCurrentWeekIndex] = useState(0);

  const currentWeek = plan.weeks[currentWeekIndex];

  const handlePrevWeek = () => {
    setCurrentWeekIndex(prev => Math.max(0, prev - 1));
  };

  const handleNextWeek = () => {
    setCurrentWeekIndex(prev => Math.min(plan.weeks.length - 1, prev + 1));
  };

  // Get session for a specific day
  const getSessionForDay = (dayName: string): AIGeneratedSession | undefined => {
    return currentWeek?.sessions.find(s => s.dayOfWeek === dayName);
  };

  // Get sport icon
  const getSportIcon = (sport: string) => {
    switch (sport) {
      case 'bike':
        return <BikeIcon fontSize="small" />;
      case 'run':
        return <RunIcon fontSize="small" />;
      default:
        return <RestIcon fontSize="small" />;
    }
  };

  // Render session card
  const renderSessionCard = (session: AIGeneratedSession | undefined, dayName: string) => {
    if (!session || session.sessionType === 'rest') {
      return (
        <Card
          variant="outlined"
          sx={{
            height: '100%',
            minHeight: isMobile ? 80 : 120,
            bgcolor: 'action.hover',
            opacity: 0.7,
          }}
        >
          <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
            <Typography variant="caption" color="text.secondary" display="block">
              {dayName.slice(0, 3)}
            </Typography>
            <Box display="flex" alignItems="center" justifyContent="center" height="100%" mt={1}>
              <Typography variant="body2" color="text.secondary">
                Rest
              </Typography>
            </Box>
          </CardContent>
        </Card>
      );
    }

    const intensityColor = INTENSITY_COLORS[session.intensity] || theme.palette.grey[500];

    return (
      <Card
        variant="outlined"
        sx={{
          height: '100%',
          minHeight: isMobile ? 80 : 120,
          borderLeft: 3,
          borderLeftColor: intensityColor,
        }}
      >
        <CardContent sx={{ py: 1, px: 1.5, '&:last-child': { pb: 1 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              {dayName.slice(0, 3)}
            </Typography>
            <Box display="flex" alignItems="center">
              {/* Session-level info icon */}
              {session.note && (
                <NoteInfoModal
                  title={`${dayName} - ${session.intensity.charAt(0).toUpperCase() + session.intensity.slice(1)} Session`}
                  note={session.note}
                  iconSize="small"
                />
              )}
              {getSportIcon(session.sport)}
            </Box>
          </Box>

          <Typography variant="body2" fontWeight={600} noWrap>
            {session.intensity.charAt(0).toUpperCase() + session.intensity.slice(1)}
          </Typography>

          <Box display="flex" gap={0.5} mt={0.5} flexWrap="wrap">
            <Chip
              label={`${session.durationMin}m`}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
            <Chip
              label={`${session.predictedTSS} TSS`}
              size="small"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Box>

          {!isMobile && session.intensityNotes && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                mt: 0.5,
                fontSize: '0.65rem',
                lineHeight: 1.3,
              }}
            >
              {session.intensityNotes}
            </Typography>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(8px)',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" fontWeight={600}>
              📅 Training Plan Preview
            </Typography>
            {isMock && <Chip label="Demo" size="small" color="warning" sx={{ fontWeight: 500 }} />}
          </Box>
          <IconButton onClick={onCancel} size="small" aria-label="Close preview">
            <CancelIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Mock Mode Alert */}
        {isMock && (
          <Box sx={{ mb: 2 }}>
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'warning.light',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="body2" fontWeight={500}>
                ⚠️ Demo Mode: This is a sample plan generated without AI.
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Configure the AI agent in Azure to get personalized training plans based on your
                data.
              </Typography>
            </Box>
          </Box>
        )}

        {/* Summary */}
        <Box
          display="flex"
          gap={2}
          mb={2}
          flexWrap="wrap"
          justifyContent="center"
          sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1.5 }}
        >
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={600}>
              {plan.totalWeeks}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Weeks
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={600}>
              {plan.summary.totalSessions}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Workouts
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={600}>
              {plan.summary.avgWeeklyTSS}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Avg TSS/Week
            </Typography>
          </Box>
          <Box textAlign="center">
            <Typography variant="h6" fontWeight={600}>
              {plan.summary.totalTSS}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total TSS
            </Typography>
          </Box>
          {/* Plan-level info icon */}
          {plan.note && (
            <Box display="flex" alignItems="center">
              <NoteInfoModal
                title="Plan Overview"
                note={plan.note}
                iconSize="medium"
                iconColor="primary"
              />
            </Box>
          )}
        </Box>

        {/* Week navigation */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <IconButton
            onClick={handlePrevWeek}
            disabled={currentWeekIndex === 0}
            aria-label="Previous week"
          >
            <PrevIcon />
          </IconButton>

          <Box textAlign="center">
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography variant="subtitle1" fontWeight={600}>
                Week {currentWeek?.weekNumber} of {plan.totalWeeks}
              </Typography>
              {/* Week-level info icon */}
              {currentWeek?.note && (
                <NoteInfoModal
                  title={`Week ${currentWeek.weekNumber} Explanation`}
                  note={currentWeek.note}
                  iconSize="small"
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {currentWeek?.weekStartDate} → {currentWeek?.weekEndDate}
            </Typography>
            <Typography variant="caption" color="primary.main" display="block">
              Target: {currentWeek?.targetTSS} TSS | Planned: {currentWeek?.actualTSS} TSS
            </Typography>
          </Box>

          <IconButton
            onClick={handleNextWeek}
            disabled={currentWeekIndex === plan.weeks.length - 1}
            aria-label="Next week"
          >
            <NextIcon />
          </IconButton>
        </Box>

        {/* Weekly calendar grid */}
        <Box
          display="grid"
          gridTemplateColumns={isMobile ? 'repeat(2, 1fr)' : 'repeat(7, 1fr)'}
          gap={1}
        >
          {DAYS_OF_WEEK.map(day => (
            <Box key={day}>{renderSessionCard(getSessionForDay(day), day)}</Box>
          ))}
        </Box>

        {/* Intensity legend */}
        <Box display="flex" gap={1} mt={2} flexWrap="wrap" justifyContent="center">
          {Object.entries(INTENSITY_COLORS).map(([intensity, color]) => (
            <Box key={intensity} display="flex" alignItems="center" gap={0.5}>
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: color,
                  borderRadius: 0.5,
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {intensity.charAt(0).toUpperCase() + intensity.slice(1)}
              </Typography>
            </Box>
          ))}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          variant="outlined"
          onClick={onCancel}
          startIcon={<CancelIcon />}
          aria-label="Cancel and discard plan"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          startIcon={<ConfirmIcon />}
          aria-label="Confirm and apply training plan"
        >
          Apply Plan
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanPreviewDialog;
